/* eslint-disable jsx-a11y/no-static-element-interactions */
import type { ToggleTypeCategoryItem } from '@/types/toggle-type';
import clsx from 'clsx';
import styles from './styles.module.scss';
import { useEffect, useRef, useState } from 'react';

interface ToggleTypeCategoryBarProps {
    categories: ToggleTypeCategoryItem[];
    selectedCategoryId: string;
    onSelect: (id: string) => void;
    isHorizontal: boolean;
}

const ToggleTypeCategoryBar = ({ categories, selectedCategoryId, onSelect, isHorizontal }: ToggleTypeCategoryBarProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const overflows = el.scrollWidth > el.clientWidth;
        setIsOverflowing(overflows);
    }, [categories]);

    const isDown = useRef(false);
    const startX = useRef(0);
    const scrollLeft = useRef(0);

    const handleMouseDown = (e: { pageX: number; }) => {
        const el = containerRef.current;
        if (!el) return;
        isDown.current = true;
        el.style.cursor = 'grabbing';
        startX.current = e.pageX - el.offsetLeft;
        scrollLeft.current = el.scrollLeft;
    };

    const handleMouseLeave = () => {
        isDown.current = false;
        if (containerRef.current) {
            containerRef.current.style.cursor = 'grab';
        }
    };

    const handleMouseUp = () => {
        isDown.current = false;
        if (containerRef.current) {
            containerRef.current.style.cursor = 'grab';
        }
    };

    const handleMouseMove = (e: { preventDefault: () => void; pageX: number; }) => {
        if (!isDown.current) return;
        const el = containerRef.current;
        if (!el) return;
        e.preventDefault();
        const x = e.pageX - el.offsetLeft;
        const walk = (x - startX.current) * 2;
        el.scrollLeft = scrollLeft.current - walk;
    };

    return (
        <div
            ref={containerRef}
            className={clsx(styles.bar, {
                [styles['bar--horizontal']]: isHorizontal,
                [styles['bar--vertical']]: !isHorizontal,
                [styles['bar--overflow']]: isOverflowing,
            })}
            data-region-nav
            data-region-order={4}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
        >
            {categories.map((cat, index) => {
                const isActive = String(cat.id) === String(selectedCategoryId);
                return (
                    <button
                        key={cat.id}
                        type="button"
                        role="radio"
                        onClick={() => onSelect(cat.id)}
                        className={clsx(styles.pill, 'focus-border')}
                        tabIndex={4 + index}
                        data-interactive
                        aria-pressed={isActive}
                        aria-checked={isActive}
                    >
                        {cat.name}
                    </button>
                );
            })}
        </div>
    );
};

export default ToggleTypeCategoryBar;
